import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import {
  BoxIcon,
  Button,
  CartIcon,
  ChartIcon,
  DemoButton,
  Orb,
  PlayIcon,
  SplitText,
  TagIcon,
  useAnchor,
  WrenchIcon
} from 'components';
import { quote } from 'src/utils/format';
import { gsap, useGSAP } from 'src/utils/gsap';
import { prefersReducedMotion } from 'src/utils/motion';

import { usePage } from '../../pages';
import { LOGOS } from './logos';

import style from './Hero.module.scss';

// Card icons referenced by name from the texts
const ICONS = { cart: CartIcon, box: BoxIcon, wrench: WrenchIcon, chart: ChartIcon, tag: TagIcon };

const Hero = () => {
  const { t, i18n } = useTranslation('page');
  const { id, raisedTranscript } = usePage();
  const onAnchorClick = useAnchor();
  const sectionRef = useRef(null);
  const scenes = t(`${id}.hero.scenes`, { returnObjects: true });

  const [scene, setScene] = useState(0);
  const [said, setSaid] = useState(0);
  const [typed, setTyped] = useState(null);
  const [status, setStatus] = useState('done');
  const [cardOut, setCardOut] = useState(false);
  const [orbMode, setOrbMode] = useState('idle');

  // Voice scenario loop: listen and type the command, think, show the action card
  useEffect(() => {
    if (prefersReducedMotion) return;

    const timers = new Set();
    const wait = (ms) =>
      new Promise((resolve) => {
        const id = setTimeout(() => {
          timers.delete(id);
          resolve();
        }, ms);
        timers.add(id);
      });
    const commandLength = (k) => quote(i18n.t(`${id}.hero.scenes.${k}.said`, { ns: 'page' }), i18n.language).length;

    const run = async () => {
      await wait(3800);
      for (let k = 1; ; k = (k + 1) % scenes.length) {
        setCardOut(true);
        await wait(650);
        setStatus('listen');
        setOrbMode('speak');
        setSaid(k);
        for (let i = 1; ; i++) {
          setTyped(i);
          if (i >= commandLength(k)) break;
          await wait(32 + Math.random() * 40);
        }
        await wait(350);
        setTyped(null);
        setOrbMode('think');
        setStatus('think');
        await wait(1100);
        setOrbMode('idle');
        setScene(k);
        setCardOut(false);
        setStatus('done');
        await wait(4600);
      }
    };

    run();
    return () => timers.forEach(clearTimeout);
  }, [i18n]);

  useGSAP(
    () => {
      if (prefersReducedMotion) return;

      const q = gsap.utils.selector(sectionRef);
      gsap
        .timeline({ defaults: { ease: 'expo.out' } })
        .from(document.querySelector('[data-nav-bar]'), { y: -30, opacity: 0, duration: 1.2 })
        .from(q('h1 .wi'), { yPercent: 115, duration: 1.4, stagger: 0.05 }, 0.15)
        .from(q('[data-intro]'), { y: 26, opacity: 0, duration: 1.2, stagger: 0.09 }, 0.75)
        .from(q('[data-stage]'), { scale: 0.82, opacity: 0, duration: 2 }, 0.3)
        .from(q('[data-stage-item]'), { y: 30, opacity: 0, duration: 1.2, stagger: 0.12, clearProps: 'opacity,transform' }, 1.1)
        .from(q('[data-band]'), { y: 30, opacity: 0, duration: 1.4 }, 1.1);

      gsap.to(q('[data-stage]'), {
        yPercent: -12,
        ease: 'none',
        scrollTrigger: { trigger: sectionRef.current, start: 'top top', end: 'bottom top', scrub: true }
      });
    },
    { scope: sectionRef }
  );

  const command = quote(scenes[said].said, i18n.language);
  const card = scenes[scene];
  const CardIcon = ICONS[card.icon];

  return (
    <section
      id='hero'
      ref={sectionRef}
      className={cn('sec tone-dark', style.hero)}
      data-tone='dark'
    >
      <div
        className={style.bg}
        aria-hidden='true'
      >
        <div className={style.gridLines} />
        <div className={cn(style.glow, style.g1)} />
        <div className={cn(style.glow, style.g2)} />
      </div>

      <div className={cn('wrap', style.grid)}>
        <div className={style.copy}>
          <SplitText
            as='h1'
            className='h-hero'
          >
            {t(`${id}.hero.title`)} <span className='serif-i accent-v'>{t(`${id}.hero.titleAccent`)}</span>
          </SplitText>
          <p
            className='lead'
            data-intro
          >
            {t(`${id}.hero.lead`)}
          </p>
          <div
            className='cta-row'
            data-intro
          >
            <DemoButton size='lg'>{t(`${id}.cta`)}</DemoButton>
            <Button
              href='#film'
              variant='ghost'
              size='lg'
              onClick={onAnchorClick}
            >
              <PlayIcon />
              {t(`${id}.hero.seeHow`)}
            </Button>
          </div>
          <p
            className={style.trust}
            data-intro
          >
            <span
              className={style.avatars}
              aria-hidden='true'
            >
              <i />
              <i />
              <i />
            </span>
            {t(`${id}.hero.trust`)}
          </p>
        </div>

        <div
          className={cn(style.stage, raisedTranscript && style.raised)}
          data-stage
        >
          <Orb
            className={style.orb}
            count={1500}
            scale={0.3}
            mode={orbMode}
          />
          {t(`${id}.hero.chips`, { returnObjects: true }).map((chip, i) => (
            <span
              key={chip}
              className={cn(style.chip, style[`chip${i + 1}`])}
              aria-hidden='true'
            >
              {chip}
            </span>
          ))}
          <div
            className={style.status}
            data-s={status}
            data-stage-item
          >
            <span className={style.pulse} />
            <span>{t(`${id}.hero.status.${status}`)}</span>
          </div>
          <div
            className={style.transcript}
            data-stage-item
          >
            <span className='mono-label'>{t(`${id}.hero.saidLabel`)}</span>
            <p>
              {typed === null ? (
                command
              ) : (
                <>
                  {command.slice(0, typed)}
                  <span className={style.caret} />
                </>
              )}
            </p>
          </div>
          <div
            className={cn(style.card, cardOut && style.out)}
            aria-live='polite'
            data-stage-item
          >
            <div className={style.cardHead}>
              <span className={style.cardIcon}>
                <CardIcon />
              </span>
              <div>
                <p className={style.kicker}>{card.kicker}</p>
                <p className={style.cardTitle}>{card.title}</p>
              </div>
              <span className={cn('chip', `chip-${card.tone}`)}>{card.chip}</span>
            </div>
            <div className={style.rows}>
              {card.rows.map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <b>{value}</b>
                </div>
              ))}
            </div>
            <div className={style.actions}>
              {card.actions.map((action, i) => (
                <Button
                  key={action}
                  size='sm'
                  variant={i === 0 ? 'primary' : 'ghost'}
                >
                  {action}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        className={style.band}
        data-band
      >
        <div className={style.bandIn}>
          <div className={style.bandStat}>
            <p
              className={style.bandNum}
              aria-hidden='true'
            >
              250<em>+</em>
            </p>
            <p className={style.bandText}>
              <b>{t(`${id}.hero.trustBand.title`)}</b>
              <span>{t(`${id}.hero.trustBand.text`)}</span>
            </p>
          </div>
          <div className={style.marquee}>
            <div className={style.track}>
              {[0, 1].map((set) => (
                <div
                  key={set}
                  className={style.set}
                  aria-hidden={set === 1 || undefined}
                >
                  {LOGOS.map((logo) => (
                    <img
                      key={logo.alt}
                      src={logo.src}
                      alt={set === 0 ? logo.alt : ''}
                      className={style[logo.tone]}
                      decoding='async'
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
